/**
 * Plus Codes (Open Location Code) — décodeur et encodeur, SANS dépendance.
 *
 * Pourquoi : à Bamako beaucoup d'adresses n'ont ni rue ni numéro, mais Google Maps donne à chaque
 * point un Plus Code (« 9C3W9QCJ+2VX », ou « 9QCJ+2VX Bamako » en version courte). Un client peut le
 * lire au téléphone : l'opératrice le tape, la console place l'épingle. Aucun appel réseau.
 *
 * Spécification : https://github.com/google/open-location-code/blob/main/Documentation/Specification.md
 *   • alphabet de 20 caractères « 23456789CFGHJMPQRVWX » (pas de voyelles ni de chiffres ambigus) ;
 *   • 10 premiers caractères = 5 paires latitude/longitude (20°, 1°, 1/20°, 1/400°, 1/8000°) ;
 *   • « + » après le 8e caractère ; « 0 » = remplissage (code moins précis) ;
 *   • au-delà de 10 caractères : une grille de 5 lignes × 4 colonnes par caractère ;
 *   • un code COURT (moins de 8 caractères avant « + ») est relatif à un lieu de référence : on retrouve
 *     le code complet le plus proche de ce lieu — ici le centre de Bamako par défaut.
 *
 * Tout est en entiers (1/8000° pour les paires, puis 5 × 4 par caractère de grille) : aucune erreur
 * de flottant dans la lecture des caractères.
 */
import { BAMAKO } from './geo'

export const ALPHABET = '23456789CFGHJMPQRVWX'
export const SEPARATEUR = '+'
export const REMPLISSAGE = '0'
const POSITION_SEPARATEUR = 8
const LONGUEUR_PAIRES = 10
const LONGUEUR_MAX = 15
const BASE = 20
const LIGNES_GRILLE = 5
const COLONNES_GRILLE = 4
/** Valeurs finales d'un code de 10 caractères : 1/8000° ; la grille subdivise 5 fois 5 × 4. */
const PRECISION_LAT = 8000 * LIGNES_GRILLE ** 5
const PRECISION_LNG = 8000 * COLONNES_GRILLE ** 5

export interface ZonePlusCode {
  latBas: number
  lngBas: number
  latHaut: number
  lngHaut: number
  latCentre: number
  lngCentre: number
  /** Nombre de caractères significatifs (hors « + » et remplissage). */
  longueur: number
}

const indice = (c: string): number => ALPHABET.indexOf(c)

/** Syntaxe d'un code COMPLET ou COURT (pas encore sa position). */
export function estPlusCodeValide(code: string): boolean {
  if (typeof code !== 'string' || code.length < 3) return false
  const sep = code.indexOf(SEPARATEUR)
  // Un seul « + », à une position paire, au plus en 8e position.
  if (sep === -1 || sep !== code.lastIndexOf(SEPARATEUR) || sep > POSITION_SEPARATEUR || sep % 2 === 1) return false
  const c = code.toUpperCase()
  // Un seul caractère après « + » est interdit.
  if (c.length - sep - 1 === 1) return false
  // Remplissage : en tête de la partie avant « + », par paires, et alors RIEN après « + ».
  const pad = c.indexOf(REMPLISSAGE)
  if (pad !== -1) {
    if (sep < POSITION_SEPARATEUR) return false
    if (pad === 0) return false
    const fin = c.lastIndexOf(REMPLISSAGE)
    const run = c.slice(pad, fin + 1)
    if (run.length % 2 === 1 || /[^0]/.test(run) || fin !== sep - 1 || c.length > sep + 1) return false
  }
  if (c.length > LONGUEUR_MAX + 1) return false
  for (const ch of c) {
    if (ch !== SEPARATEUR && ch !== REMPLISSAGE && indice(ch) === -1) return false
  }
  // Un code COMPLET : la première paire doit tenir dans le globe.
  if (sep === POSITION_SEPARATEUR) {
    if (indice(c[0] as string) > 8) return false // latitude : 9 valeurs de 20° = 180°
    if (indice(c[1] as string) > 17) return false // longitude : 18 valeurs de 20° = 360°
  }
  return true
}

export function estCodeCourt(code: string): boolean {
  return estPlusCodeValide(code) && code.indexOf(SEPARATEUR) < POSITION_SEPARATEUR
}

export function estCodeComplet(code: string): boolean {
  return estPlusCodeValide(code) && code.indexOf(SEPARATEUR) === POSITION_SEPARATEUR
}

/** Décode un code COMPLET en zone. Lève une `Error` si le code n'est pas complet et valide. */
export function decoder(code: string): ZonePlusCode {
  if (!estCodeComplet(code)) throw new Error('plus_code_non_complet')
  const c = code.toUpperCase().replace(SEPARATEUR, '').replace(/0+$/, '')
  const significatifs = c.slice(0, LONGUEUR_MAX)

  // Paires : entiers en 1/8000°.
  let lat = -90 * 8000
  let lng = -180 * 8000
  let resolution = 20 * 8000
  const nbPaires = Math.min(significatifs.length, LONGUEUR_PAIRES)
  for (let i = 0; i < nbPaires; i += 2) {
    lat += indice(significatifs[i] as string) * resolution
    lng += (i + 1 < nbPaires ? indice(significatifs[i + 1] as string) : 0) * resolution
    if (i + 2 < LONGUEUR_PAIRES) resolution /= BASE
  }
  // Hauteur/largeur d'une case de la dernière paire lue.
  const pas = 20 * 8000 / BASE ** (Math.ceil(nbPaires / 2) - 1)
  let latHaut = lat + pas
  let lngHaut = lng + pas
  let latBas = lat
  let lngBas = lng
  let dLat = pas
  let dLng = pas
  // Grille : chaque caractère divise la case en 5 lignes × 4 colonnes.
  for (let i = LONGUEUR_PAIRES; i < significatifs.length; i++) {
    const d = indice(significatifs[i] as string)
    dLat /= LIGNES_GRILLE
    dLng /= COLONNES_GRILLE
    latBas += Math.floor(d / COLONNES_GRILLE) * dLat
    lngBas += (d % COLONNES_GRILLE) * dLng
    latHaut = latBas + dLat
    lngHaut = lngBas + dLng
  }
  const vers = (v: number) => v / 8000
  return {
    latBas: vers(latBas),
    lngBas: vers(lngBas),
    latHaut: vers(latHaut),
    lngHaut: vers(lngHaut),
    latCentre: vers((latBas + latHaut) / 2),
    lngCentre: vers((lngBas + lngHaut) / 2),
    longueur: significatifs.length,
  }
}

/** Encode un point en code complet de `longueur` caractères (10 par défaut : une case d'environ 14 m). */
export function encoder(lat: number, lng: number, longueur = LONGUEUR_PAIRES): string {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('coordonnees_invalides')
  if (longueur < 2 || longueur > LONGUEUR_MAX || (longueur < POSITION_SEPARATEUR && longueur % 2 === 1)) {
    throw new Error('longueur_invalide')
  }
  let la = Math.min(90, Math.max(-90, lat))
  let lo = lng
  while (lo < -180) lo += 360
  while (lo >= 180) lo -= 360
  // Le pôle nord n'a pas de case : on le replace dans la dernière.
  if (la === 90) la -= 1 / PRECISION_LAT
  let latVal = Math.floor(Math.round((la + 90) * PRECISION_LAT * 1e6) / 1e6)
  let lngVal = Math.floor(Math.round((lo + 180) * PRECISION_LNG * 1e6) / 1e6)
  let code = ''
  if (longueur > LONGUEUR_PAIRES) {
    for (let i = 0; i < LONGUEUR_MAX - LONGUEUR_PAIRES; i++) {
      code = (ALPHABET[(latVal % LIGNES_GRILLE) * COLONNES_GRILLE + (lngVal % COLONNES_GRILLE)] as string) + code
      latVal = Math.floor(latVal / LIGNES_GRILLE)
      lngVal = Math.floor(lngVal / COLONNES_GRILLE)
    }
  } else {
    latVal = Math.floor(latVal / LIGNES_GRILLE ** 5)
    lngVal = Math.floor(lngVal / COLONNES_GRILLE ** 5)
  }
  for (let i = 0; i < LONGUEUR_PAIRES / 2; i++) {
    code = (ALPHABET[lngVal % BASE] as string) + code
    code = (ALPHABET[latVal % BASE] as string) + code
    latVal = Math.floor(latVal / BASE)
    lngVal = Math.floor(lngVal / BASE)
  }
  code = code.slice(0, POSITION_SEPARATEUR) + SEPARATEUR + code.slice(POSITION_SEPARATEUR)
  if (longueur >= POSITION_SEPARATEUR) return code.slice(0, longueur + 1)
  return code.slice(0, longueur) + REMPLISSAGE.repeat(POSITION_SEPARATEUR - longueur) + SEPARATEUR
}

/**
 * Retrouve le code COMPLET le plus proche du lieu de référence pour un code COURT.
 * Un code court perd ses premiers caractères : on les reprend de la référence, puis on recale d'une
 * case si le résultat tombe de l'autre côté de la référence.
 */
export function recupererProche(court: string, refLat: number, refLng: number): string {
  if (!estCodeCourt(court)) throw new Error('plus_code_non_court')
  const c = court.toUpperCase()
  const remplissage = POSITION_SEPARATEUR - c.indexOf(SEPARATEUR)
  const resolution = BASE ** (2 - remplissage / 2) // degrés couverts par la partie perdue
  const moitie = resolution / 2
  const la = Math.min(90, Math.max(-90, refLat))
  let lo = refLng
  while (lo < -180) lo += 360
  while (lo >= 180) lo -= 360
  const prefixe = encoder(la, lo).slice(0, remplissage)
  const zone = decoder(prefixe + c)
  let latC = zone.latCentre
  let lngC = zone.lngCentre
  if (la + moitie < latC && latC - resolution >= -90) latC -= resolution
  else if (la - moitie > latC && latC + resolution <= 90) latC += resolution
  if (lo + moitie < lngC) lngC -= resolution
  else if (lo - moitie > lngC) lngC += resolution
  return encoder(latC, lngC, zone.longueur)
}

export type LecturePlusCode =
  | { ok: true; lat: number; lng: number; code: string; court: boolean; precisionM: number }
  | { ok: false; raison: string }

const METRES_PAR_DEGRE = 111_320

/**
 * Lit ce que l'opératrice a tapé : « 9C3W9QCJ+2VX », « 9QCJ+2VX », « 9QCJ+2VX Bamako, Mali » (version de
 * Google Maps : seul le premier mot qui contient « + » compte). Un code COURT se rattache à `reference`
 * (le centre de Bamako par défaut). Le résultat est le CENTRE de la case, avec sa précision en mètres.
 */
export function lirePlusCode(saisie: string, reference: readonly [number, number] = BAMAKO): LecturePlusCode {
  const mot = saisie
    .trim()
    .split(/[\s,;]+/)
    .find((m) => m.includes(SEPARATEUR))
  if (!mot) return { ok: false, raison: 'Un Plus Code contient un « + » (exemple : 9QCJ+2VX).' }
  if (!estPlusCodeValide(mot)) {
    return { ok: false, raison: `« ${mot} » n'est pas un Plus Code valide. Vérifiez les caractères (pas de voyelles, pas de O ni de I).` }
  }
  const court = estCodeCourt(mot)
  if (court && mot.indexOf(SEPARATEUR) < 2) {
    return { ok: false, raison: 'Ce code est trop court pour être placé : il faut au moins 4 caractères avant le « + ».' }
  }
  const complet = court ? recupererProche(mot, reference[0], reference[1]) : mot.toUpperCase()
  const z = decoder(complet)
  const hauteurM = (z.latHaut - z.latBas) * METRES_PAR_DEGRE
  return { ok: true, lat: z.latCentre, lng: z.lngCentre, code: complet, court, precisionM: Math.round(hauteurM) }
}
